import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  Lightbulb,
  MessageCircle,
  Plus,
  RefreshCcw,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import clsx from 'clsx';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { petTipsApi, type PetTip, type PetTipsConfig } from '@/lib/api/petTips';

const queryKey = ['pet-tips', 'config'] as const;

const tipCategories = [
  { value: 'onboarding', label: '新手引导' },
  { value: 'feature', label: '功能玩法' },
  { value: 'daily', label: '每日提醒' },
  { value: 'support', label: '情绪支持' },
  { value: 'relationship', label: '关系感情' },
  { value: 'decision', label: '选择决策' },
  { value: 'self', label: '自我了解' },
  { value: 'tarot', label: '抽牌塔罗' },
  { value: 'utility', label: '效率工具' },
  { value: 'share', label: '分享传播' },
  { value: 'general', label: '通用' },
] as const;

function createTip(sortOrder: number): PetTip {
  return {
    id: `tip_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    text_zh: '',
    text_en: '',
    is_enabled: true,
    sort_order: sortOrder,
    category: 'general',
    weight: 1,
  };
}

function sortTips(tips: PetTip[]) {
  return [...tips].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
}

function normalizeTips(tips: PetTip[]) {
  return sortTips(tips).map((tip, index) => ({
    ...tip,
    category: tip.category || 'general',
    weight: Number.isFinite(tip.weight) ? tip.weight : 1,
    sort_order: index * 10,
  }));
}

function normalizeConfig(config: PetTipsConfig): PetTipsConfig {
  return {
    ...config,
    daily_auto_limit: config.daily_auto_limit ?? 4,
    dismiss_cooldown_seconds: config.dismiss_cooldown_seconds ?? 600,
    seen_cooldown_days: config.seen_cooldown_days ?? 7,
    tips: normalizeTips(config.tips),
  };
}

function validateConfig(config: PetTipsConfig) {
  const errors: string[] = [];

  if (config.min_interval_seconds < 15 || config.min_interval_seconds > 3600) {
    errors.push('最小间隔必须在 15-3600 秒之间');
  }
  if (config.max_interval_seconds < 15 || config.max_interval_seconds > 3600) {
    errors.push('最大间隔必须在 15-3600 秒之间');
  }
  if (config.min_interval_seconds > config.max_interval_seconds) {
    errors.push('最小间隔不能大于最大间隔');
  }
  if (config.visible_seconds < 2 || config.visible_seconds > 20) {
    errors.push('气泡显示时长必须在 2-20 秒之间');
  }
  if (config.daily_auto_limit < 1 || config.daily_auto_limit > 20) {
    errors.push('每天自动弹出次数必须在 1-20 次之间');
  }
  if (config.dismiss_cooldown_seconds < 60 || config.dismiss_cooldown_seconds > 86400) {
    errors.push('关闭冷却必须在 60-86400 秒之间');
  }
  if (config.seen_cooldown_days < 0 || config.seen_cooldown_days > 30) {
    errors.push('看过冷却必须在 0-30 天之间');
  }

  const seen = new Set<string>();
  config.tips.forEach((tip, index) => {
    const label = `第 ${index + 1} 条`;
    if (!tip.id.trim()) errors.push(`${label} 缺少 ID`);
    if (seen.has(tip.id)) errors.push(`${label} ID 重复：${tip.id}`);
    seen.add(tip.id);
    if (!tip.text_zh.trim()) errors.push(`${label} 缺少中文文案`);
    if (!tip.text_en.trim()) errors.push(`${label} 缺少英文文案`);
    if (tip.text_zh.length > 120) errors.push(`${label} 中文文案超过 120 字`);
    if (tip.text_en.length > 120) errors.push(`${label} 英文文案超过 120 字`);
    if (!tip.category.trim()) errors.push(`${label} 缺少分类`);
    if (tip.weight < 1 || tip.weight > 20) errors.push(`${label} 权重必须在 1-20 之间`);
  });

  return errors;
}

export function PetTipsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: petTipsApi.getConfig,
  });
  const saveMutation = useMutation({
    mutationFn: petTipsApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Tips 配置已保存');
    },
    onError: (err: Error) => {
      toast.error(`保存失败：${err.message}`);
    },
  });

  const [draft, setDraft] = useState<PetTipsConfig | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewLocale, setPreviewLocale] = useState<'zh' | 'en'>('zh');

  useEffect(() => {
    if (!data) return;
    const normalized = normalizeConfig(data);
    setDraft(normalized);
    setSelectedId((current) => current ?? normalized.tips[0]?.id ?? null);
  }, [data]);

  const sortedTips = useMemo(() => (draft ? sortTips(draft.tips) : []), [draft]);
  const selectedTip = sortedTips.find((tip) => tip.id === selectedId) ?? sortedTips[0] ?? null;
  const enabledCount = sortedTips.filter((tip) => tip.is_enabled).length;
  const hasChanges = draft && data
    ? JSON.stringify(draft) !== JSON.stringify(normalizeConfig(data))
    : false;

  const updateDraft = (patch: Partial<PetTipsConfig>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const updateTip = (tipId: string, patch: Partial<PetTip>) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        tips: current.tips.map((tip) => (tip.id === tipId ? { ...tip, ...patch } : tip)),
      };
    });
  };

  const updateTipId = (tipId: string, nextId: string) => {
    const normalizedId = nextId.trim();
    updateTip(tipId, { id: normalizedId });
    setSelectedId(normalizedId);
  };

  const addTip = () => {
    setDraft((current) => {
      if (!current) return current;
      const nextTip = createTip(current.tips.length * 10);
      setSelectedId(nextTip.id);
      return { ...current, tips: [...current.tips, nextTip] };
    });
  };

  const removeTip = (tipId: string) => {
    setDraft((current) => {
      if (!current) return current;
      const nextTips = normalizeTips(current.tips.filter((tip) => tip.id !== tipId));
      setSelectedId(nextTips[0]?.id ?? null);
      return { ...current, tips: nextTips };
    });
  };

  const moveTip = (tipId: string, direction: -1 | 1) => {
    setDraft((current) => {
      if (!current) return current;
      const tips = sortTips(current.tips);
      const index = tips.findIndex((tip) => tip.id === tipId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= tips.length) return current;
      const [item] = tips.splice(index, 1);
      tips.splice(nextIndex, 0, item);
      return { ...current, tips: normalizeTips(tips) };
    });
  };

  const handleSave = () => {
    if (!draft) return;
    const normalized = normalizeConfig(draft);
    const errors = validateConfig(normalized);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    saveMutation.mutate(normalized);
  };

  const handleResetDraft = () => {
    if (!data) return;
    const normalized = normalizeConfig(data);
    setDraft(normalized);
    setSelectedId(normalized.tips[0]?.id ?? null);
  };

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">加载中...</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-red-600">
        加载失败：{(error as Error).message}
      </div>
    );
  }

  if (!draft) {
    return <div className="p-6 text-sm text-gray-500">暂无配置</div>;
  }

  const previewText = selectedTip
    ? previewLocale === 'zh'
      ? selectedTip.text_zh || '顶部下拉，可以快速抽牌'
      : selectedTip.text_en || 'Pull down from the top to draw a quick card'
    : '顶部下拉，可以快速抽牌';

  return (
    <div className="min-h-full flex flex-col bg-gray-50">
      <div className="sticky top-0 z-10 px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tips 管理</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                配置端内灵动岛宠物的低频使用提示和气泡节奏
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleResetDraft} disabled={!hasChanges}>
              <RefreshCcw className="w-4 h-4 mr-1" />
              重置
            </Button>
            <Button size="sm" onClick={handleSave} loading={saveMutation.isPending} disabled={!hasChanges}>
              <Save className="w-4 h-4 mr-1" />
              保存更改
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 gap-5 xl:grid-cols-[320px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)_320px]">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col min-h-[360px] xl:sticky xl:top-24 xl:max-h-[calc(100dvh_-_8rem)]">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">文案库</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {enabledCount} / {sortedTips.length} 条启用
                </p>
              </div>
              <Button size="sm" onClick={addTip}>
                <Plus className="w-4 h-4 mr-1" />
                添加
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {sortedTips.map((tip) => (
                <button
                  key={tip.id}
                  type="button"
                  onClick={() => setSelectedId(tip.id)}
                  className={clsx(
                    'w-full text-left rounded-lg border px-3 py-3 transition-colors',
                    selectedTip?.id === tip.id
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-start gap-2">
                    {tip.is_enabled ? (
                      <Eye className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                    <p className="text-sm font-medium leading-5 text-gray-900 break-words">
                      {tip.text_zh || '未填写中文文案'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 break-all">{tip.id}</p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {tip.category || 'general'} · 权重 {tip.weight || 1}
                    </p>
                  </div>
                </div>
              </button>
              ))}
            </div>
          </div>

          <div className="space-y-5 min-w-0">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-gray-900">全局节奏</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    控制客户端自动弹出频率。点击宠物仍可立即切换下一条。
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => updateDraft({ enabled: !draft.enabled })}
                  className={clsx(
                    'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
                    draft.enabled ? 'bg-emerald-500' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform',
                      draft.enabled ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
                <Input
                  label="最小间隔（秒）"
                  type="number"
                  min={15}
                  max={3600}
                  value={draft.min_interval_seconds}
                  onChange={(event) => updateDraft({ min_interval_seconds: Number(event.target.value) })}
                />
                <Input
                  label="最大间隔（秒）"
                  type="number"
                  min={15}
                  max={3600}
                  value={draft.max_interval_seconds}
                  onChange={(event) => updateDraft({ max_interval_seconds: Number(event.target.value) })}
                />
                <Input
                  label="显示时长（秒）"
                  type="number"
                  min={2}
                  max={20}
                  value={draft.visible_seconds}
                  onChange={(event) => updateDraft({ visible_seconds: Number(event.target.value) })}
                />
                <Input
                  label="每日自动上限"
                  type="number"
                  min={1}
                  max={20}
                  value={draft.daily_auto_limit}
                  onChange={(event) => updateDraft({ daily_auto_limit: Number(event.target.value) })}
                />
                <Input
                  label="关闭冷却（秒）"
                  type="number"
                  min={60}
                  max={86400}
                  value={draft.dismiss_cooldown_seconds}
                  onChange={(event) => updateDraft({ dismiss_cooldown_seconds: Number(event.target.value) })}
                />
                <Input
                  label="看过冷却（天）"
                  type="number"
                  min={0}
                  max={30}
                  value={draft.seen_cooldown_days}
                  onChange={(event) => updateDraft({ seen_cooldown_days: Number(event.target.value) })}
                />
              </div>
            </div>

            {selectedTip ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-semibold text-gray-900">编辑 Tip</h2>
                    <p className="text-sm text-gray-500 mt-0.5">文案建议短句化，避免遮挡顶部聊天区。</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => moveTip(selectedTip.id, -1)}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => moveTip(selectedTip.id, 1)}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => removeTip(selectedTip.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_120px_160px_120px] gap-4">
                  <Input
                    label="Tip ID"
                    value={selectedTip.id}
                    onChange={(event) => updateTipId(selectedTip.id, event.target.value)}
                  />
                  <Input
                    label="排序"
                    type="number"
                    value={selectedTip.sort_order}
                    onChange={(event) => updateTip(selectedTip.id, { sort_order: Number(event.target.value) })}
                  />
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">分类</span>
                    <select
                      value={selectedTip.category}
                      onChange={(event) => updateTip(selectedTip.id, { category: event.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                    >
                      {tipCategories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Input
                    label="权重"
                    type="number"
                    min={1}
                    max={20}
                    value={selectedTip.weight}
                    onChange={(event) => updateTip(selectedTip.id, { weight: Number(event.target.value) })}
                  />
                </div>

                <Textarea
                  label="中文文案"
                  rows={3}
                  value={selectedTip.text_zh}
                  onChange={(event) => updateTip(selectedTip.id, { text_zh: event.target.value })}
                  helperText={`${selectedTip.text_zh.length}/120`}
                />
                <Textarea
                  label="英文文案"
                  rows={3}
                  value={selectedTip.text_en}
                  onChange={(event) => updateTip(selectedTip.id, { text_en: event.target.value })}
                  helperText={`${selectedTip.text_en.length}/120`}
                />

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    checked={selectedTip.is_enabled}
                    onChange={(event) => updateTip(selectedTip.id, { is_enabled: event.target.checked })}
                  />
                  启用这条 tip
                </label>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center text-gray-500">
                先添加一条 tip
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 h-fit xl:col-span-2 2xl:col-span-1 2xl:sticky 2xl:top-24">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-semibold text-gray-900">气泡预览</h2>
                <p className="text-xs text-gray-500 mt-0.5">位置跟随宠物中心线</p>
              </div>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setPreviewLocale('zh')}
                  className={clsx('px-2 py-1', previewLocale === 'zh' ? 'bg-gray-900 text-white' : 'text-gray-500')}
                >
                  中
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewLocale('en')}
                  className={clsx('px-2 py-1', previewLocale === 'en' ? 'bg-gray-900 text-white' : 'text-gray-500')}
                >
                  EN
                </button>
              </div>
            </div>

            <div className="rounded-[28px] border border-gray-200 bg-gray-950 p-3">
              <div className="relative h-[360px] sm:h-[430px] rounded-[22px] bg-[#f7f7f4] overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-7 w-28 rounded-full bg-black" />
                <div className="absolute top-12 left-0 right-0 flex flex-col items-center">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-b from-amber-200 to-amber-400 border border-amber-300 shadow-sm flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-amber-800" />
                  </div>
                  <div className="relative mt-1 max-w-[230px]">
                    <div className="absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-white border-l border-t border-gray-200" />
                    <div className="mt-2 rounded-xl bg-white border border-gray-200 px-3 py-2 shadow-md text-center text-xs leading-5 text-gray-900">
                      {previewText}
                    </div>
                  </div>
                </div>
                <div className="absolute left-5 right-5 top-40 space-y-3">
                  <div className="h-16 rounded-lg bg-white border border-gray-100" />
                  <div className="h-24 rounded-lg bg-white border border-gray-100" />
                  <div className="h-20 rounded-lg bg-white border border-gray-100" />
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.min_interval_seconds}s</div>
                <div className="text-[11px] text-gray-500">最短</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.max_interval_seconds}s</div>
                <div className="text-[11px] text-gray-500">最长</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.visible_seconds}s</div>
                <div className="text-[11px] text-gray-500">显示</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.daily_auto_limit}</div>
                <div className="text-[11px] text-gray-500">日上限</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.dismiss_cooldown_seconds}s</div>
                <div className="text-[11px] text-gray-500">关闭冷却</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-sm font-semibold text-gray-900">{draft.seen_cooldown_days}d</div>
                <div className="text-[11px] text-gray-500">看过冷却</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
